import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getQueryFn, API_URL, getAuthToken } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users, 
  CheckCircle2, 
  FileText,
  Loader2,
  AlertCircle,
  BarChart3,
  Calendar,
  Clock,
  Download,
  File,
  Image as ImageIcon,
  ExternalLink,
  X,
  Video,
  Play,
  PieChart,
  User
} from "lucide-react";
import { 
  QuestionType, 
  SurveyResponse, 
  HeatMapAnswer, 
  HeatMapClick, 
  isHeatMapAnswer,
  PickGroupRankAnswer,
  isPickGroupRankAnswer,
  getItemLabel,
  SurveySettings
} from "@/types/survey";
import { ExportOptionsDialog, ExportOptions } from "@/components/ExportOptionsDialog";
import { useDebouncedValue } from "@/hooks/use-debounce";
import { HeatMapVisualization, IndividualResponseData } from "@/components/HeatMapVisualization";
import { AppShell, PageHeader, StatCard, DataTable } from "@/components/app";

// Flatten any answer value to searchable text (no JSON.stringify)
function flattenToSearchText(value: unknown, depth = 0): string {
  if (depth > 3) return ""; // Prevent infinite recursion
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  
  if (Array.isArray(value)) {
    return value.map(v => flattenToSearchText(v, depth + 1)).join(" ");
  }
  
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    // For file uploads, extract filename (support both filename and name)
    if (typeof obj.filename === "string") return obj.filename;
    if (typeof obj.name === "string") return obj.name;
    if (typeof obj.originalName === "string") return obj.originalName;
    
    // For other objects, flatten values only (ignore keys)
    return Object.values(obj)
      .map(v => flattenToSearchText(v, depth + 1))
      .join(" ");
  }
  
  return "";
}

// Build searchable blob for a response - uses pre-built question map
function buildSearchBlob(
  response: SurveyResponse,
  questionById: Map<string, QuestionType>
): string {
  const parts: string[] = [];
  
  // Response ID
  parts.push(String(response.id));
  
  // Respondent ID if present (explicit null/undefined check to handle 0)
  if (response.respondentId !== null && response.respondentId !== undefined) {
    parts.push(String(response.respondentId));
  }
  
  // Dates - include both ISO (reliable) and locale (human-friendly)
  if (response.createdAt) {
    const d = new Date(response.createdAt);
    parts.push(d.toISOString().slice(0, 10)); // YYYY-MM-DD
    parts.push(d.toLocaleDateString());
  }
  if (response.completedAt) {
    const d = new Date(response.completedAt);
    parts.push(d.toISOString().slice(0, 10));
    parts.push(d.toLocaleDateString());
  }
  
  // Status
  parts.push(response.isComplete ? "complete completed" : "partial incomplete");
  
  // All answers - flatten each
  if (response.answers) {
    for (const questionId of Object.keys(response.answers)) {
      const answer = response.answers[questionId];
      const question = questionById.get(questionId); // O(1) lookup
      
      // For image choice, convert indices to captions
      if (question?.type === "image_choice" && question.imageOptions) {
        const indices = Array.isArray(answer) ? answer : [answer];
        for (const idx of indices) {
          if (idx === "__none__") {
            parts.push(question.imageOptions.noneOfAboveLabel || "None of the above");
          } else {
            const imgIndex = Number.parseInt(String(idx), 10);
            if (Number.isFinite(imgIndex) && imgIndex >= 0 && imgIndex < question.imageOptions.images.length) {
              const img = question.imageOptions.images[imgIndex];
              if (img?.caption) parts.push(img.caption);
              if (img?.altText) parts.push(img.altText);
            }
          }
        }
      } else {
        parts.push(flattenToSearchText(answer));
      }
    }
  }
  
  // Metadata
  if (response.metadata) {
    if (response.metadata.language) parts.push(response.metadata.language);
  }
  
  // Return lowercase - all comparisons will be lowercase
  return parts.join(" ").toLowerCase();
}

// Types for API response
interface ResponsesData {
  survey: {
    id: number;
    title: string;
    status: string;
    questions: QuestionType[];
    settings?: SurveySettings;
  };
  responses: SurveyResponse[];
  stats: {
    total: number;
    completed: number;
    completionRate: number;
  };
}

// File/Video answer with inline preview
interface FileAnswerProps {
  answer: { key: string; filename: string; size: number; mimetype: string; duration?: number };
  surveyId: number;
  responseId: number;
}

function FileAnswerDisplay({ answer, surveyId, responseId }: FileAnswerProps) {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isImage = answer.mimetype?.startsWith("image/");
  const isVideo = answer.mimetype?.startsWith("video/");

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get file icon based on mimetype
  const getFileIcon = (mimetype: string) => {
    if (mimetype?.startsWith("image/")) {
      return <ImageIcon className="h-4 w-4 text-purple-500 dark:text-purple-400" />;
    }
    if (mimetype?.startsWith("video/")) {
      return <Video className="h-4 w-4 text-red-500 dark:text-red-400" />;
    }
    if (mimetype?.includes("pdf")) {
      return <FileText className="h-4 w-4 text-red-500 dark:text-red-400" />;
    }
    return <File className="h-4 w-4 text-blue-500 dark:text-blue-400" />;
  };

  // Format duration for video
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Fetch signed URL for the file - memoized to be stable for useEffect deps
  // Accepts optional AbortSignal for proper cancellation
  const fetchSignedUrl = useCallback(async (signal?: AbortSignal): Promise<string | null> => {
    try {
      const url = API_URL 
        ? `${API_URL}/api/surveys/${surveyId}/files/signed-url`
        : `/api/surveys/${surveyId}/files/signed-url`;
      
      const token = getAuthToken();
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ key: answer.key }),
        signal,  // Pass abort signal to fetch for proper cancellation
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.url;
    } catch (error) {
      // Don't treat abort errors as failures - they're expected on cleanup
      if (error instanceof Error && error.name === "AbortError") return null;
      return null;
    }
  }, [surveyId, answer.key]);

  // Load media (image or video) on mount - track if we've started loading
  const hasStartedLoadingRef = useRef(false);
  
  useEffect(() => {
    // Only load once per mount (or when fetchSignedUrl changes)
    if (!(isImage || isVideo) || hasStartedLoadingRef.current) return;
    
    hasStartedLoadingRef.current = true;
    const abortController = new AbortController();
    
    // Setting loading state before async operation is standard pattern
    // The guard above prevents cascading - this only runs once per mount
    setIsLoadingMedia(true); // eslint-disable-line react-hooks/set-state-in-effect
    
    fetchSignedUrl(abortController.signal).then((url) => {
      // Check if aborted before updating state
      if (abortController.signal.aborted) return;
      
      if (url) {
        setMediaUrl(url);
      } else {
        setMediaError(true);
      }
      setIsLoadingMedia(false);
    });
    
    return () => { abortController.abort(); };
  }, [isImage, isVideo, fetchSignedUrl]);

  // Handle viewing non-image files
  const handleViewFile = async () => {
    const url = await fetchSignedUrl();
    if (url) {
      window.open(url, "_blank");
    } else {
      alert("Failed to load file. Please try again.");
    }
  };

  // Server-authorised download - clean redirect, no blob
  const handleDownload = () => {
    const base = API_URL
      ? `${API_URL}/api/surveys/${surveyId}/responses/${responseId}/files/${encodeURIComponent(answer.key)}/download`
      : `/api/surveys/${surveyId}/responses/${responseId}/files/${encodeURIComponent(answer.key)}/download`;

    window.location.assign(base);
  };

  // For images: show inline preview
  if (isImage) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {getFileIcon(answer.mimetype)}
          <span className="font-medium truncate">{answer.filename}</span>
          <span className="text-muted-foreground/70">({formatFileSize(answer.size)})</span>
        </div>
        
        {isLoadingMedia ? (
          <div className="bg-muted rounded-lg p-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : mediaError ? (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg p-4 text-sm">
            Failed to load image preview
          </div>
        ) : mediaUrl ? (
          <>
            <div 
              className="relative cursor-pointer group"
              onClick={() => setIsFullscreen(true)}
            >
              <img 
                src={mediaUrl} 
                alt={answer.filename}
                className="max-w-full max-h-64 rounded-lg border border-border object-contain bg-muted"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 text-white bg-black/50 px-3 py-1 rounded-full text-sm transition-opacity">
                  Click to enlarge
                </span>
              </div>
            </div>

            {/* Fullscreen modal */}
            {isFullscreen && (
              <div 
                className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer"
                onClick={() => setIsFullscreen(false)}
              >
                <button 
                  className="absolute top-4 right-4 text-white hover:text-white/70 transition-colors"
                  onClick={() => setIsFullscreen(false)}
                >
                  <X className="h-8 w-8" />
                </button>
                <img 
                  src={mediaUrl} 
                  alt={answer.filename}
                  className="max-w-full max-h-full object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </>
        ) : null}
      </div>
    );
  }

  // For videos: show inline video player
  if (isVideo) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {getFileIcon(answer.mimetype)}
          <span className="font-medium truncate">{answer.filename}</span>
          <span className="text-muted-foreground/70">
            ({formatFileSize(answer.size)}
            {answer.duration ? ` • ${formatDuration(answer.duration)}` : ""})
          </span>
        </div>
        
        {isLoadingMedia ? (
          <div className="bg-slate-900 rounded-lg p-8 flex flex-col items-center justify-center aspect-video max-w-md">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
            <span className="text-slate-400 text-sm">Loading video...</span>
          </div>
        ) : mediaError ? (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg p-4 text-sm">
            Failed to load video preview
          </div>
        ) : mediaUrl ? (
          <>
            <div 
              className="relative cursor-pointer group max-w-md"
              onClick={() => setIsFullscreen(true)}
            >
              <video 
                src={mediaUrl}
                className="w-full rounded-lg border border-border bg-slate-900"
                playsInline
                controls
                preload="metadata"
              />
              <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                <Play className="h-3 w-3" />
                Video Response
              </div>
            </div>

            {/* Fullscreen modal for video */}
            {isFullscreen && (
              <div 
                className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
                onClick={() => setIsFullscreen(false)}
              >
                <button 
                  className="absolute top-4 right-4 text-white hover:text-white/70 transition-colors z-10"
                  onClick={() => setIsFullscreen(false)}
                >
                  <X className="h-8 w-8" />
                </button>
                <video 
                  src={mediaUrl}
                  className="max-w-full max-h-full rounded-lg"
                  playsInline
                  controls
                  autoPlay
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            {/* Download button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="gap-1.5 mt-2"
              aria-label={`Download ${answer.filename}`}
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </>
        ) : null}
      </div>
    );
  }

  // For non-images: show file info with view button
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {getFileIcon(answer.mimetype)}
        <div className="min-w-0">
          <p className="font-medium text-foreground truncate">
            {answer.filename}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatFileSize(answer.size)}
          </p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={handleViewFile}
        className="gap-1 flex-shrink-0"
        aria-label={`View ${answer.filename}`}
      >
        <ExternalLink className="h-3 w-3" />
        View
      </Button>
    </div>
  );
}

export default function SurveyResponses() {
  const { id } = useParams<{ id: string }>();
  const surveyId = parseInt(id || "0");
  const { isLoading: authLoading } = useAuth();
  const [selectedResponse, setSelectedResponse] = useState<SurveyResponse | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 200);

  // Heat Map Aggregate View state
  const [heatMapSignedUrls, setHeatMapSignedUrls] = useState<Record<string, string>>({});
  const [expandedHeatMapQuestions, setExpandedHeatMapQuestions] = useState<Set<string>>(new Set());

  // Export function with options - server-authorized redirect (no blob)
  const handleExport = (options: ExportOptions) => {
    // Close dialog before redirect to avoid modal/navigation edge cases
    setShowExportDialog(false);

    const params = new URLSearchParams({
      format: options.format,
      matrixFormat: options.matrixFormat,
      includeMetadata: String(options.includeMetadata),
      includeTimestamps: String(options.includeTimestamps),
      sortOrder: options.sortOrder,
      answerFormat: options.answerFormat,
    });

    const url = API_URL
      ? `${API_URL}/api/surveys/${surveyId}/responses/export?${params.toString()}`
      : `/api/surveys/${surveyId}/responses/export?${params.toString()}`;

    // Server-authorized redirect download - browser handles file via Content-Disposition
    window.location.assign(url);
  };

  // Fetch responses data
  const { data, isLoading, isFetching, error } = useQuery<ResponsesData>({
    queryKey: [`/api/surveys/${surveyId}/responses`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!surveyId && !authLoading,
  });

  // Safe references for use in memos (before early returns)
  const survey = data?.survey;
  const responsesSafe = useMemo(() => data?.responses ?? [], [data?.responses]);

  // Build question lookup map once - O(1) lookups instead of O(n) find()
  const questionById = useMemo(() => {
    const map = new Map<string, QuestionType>();
    for (const q of survey?.questions ?? []) map.set(String(q.id), q);
    return map;
  }, [survey?.questions]);

  // Precompute search blobs once when responses/questions change
  const searchableResponses = useMemo(() => {
    return responsesSafe.map((response) => ({
      response,
      searchBlob: buildSearchBlob(response, questionById),
    }));
  }, [responsesSafe, questionById]);

  // Filter using debounced term - simple lowercase includes
  const filteredResponses = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return responsesSafe;
    return searchableResponses
      .filter(({ searchBlob }) => searchBlob.includes(term))
      .map(({ response }) => response);
  }, [searchableResponses, debouncedSearch, responsesSafe]);

  // ============================================
  // Heat Map Aggregate View Helpers (must be before early returns)
  // ============================================

  // Get all heat map questions in the survey
  const heatMapQuestions = useMemo(() => {
    return survey?.questions.filter((q) => q.type === "heat_map") || [];
  }, [survey?.questions]);

  // Collect all clicks from all responses for a given heat map question
  const getAggregateClicks = useCallback((questionId: string): HeatMapClick[] => {
    const allClicks: HeatMapClick[] = [];
    
    responsesSafe.forEach((response) => {
      const answer = response.answers?.[questionId];
      if (isHeatMapAnswer(answer) && answer.clicks) {
        allClicks.push(...answer.clicks);
      }
    });
    
    return allClicks;
  }, [responsesSafe]);

  // Get natural dimensions from first valid response (or config)
  const getHeatMapDimensions = useCallback((question: QuestionType): { width: number; height: number } => {
    // Try config first
    if (question.heatMapConfig?.imageNaturalWidth && question.heatMapConfig?.imageNaturalHeight) {
      return {
        width: question.heatMapConfig.imageNaturalWidth,
        height: question.heatMapConfig.imageNaturalHeight,
      };
    }
    
    // Fall back to first response with valid dimensions
    for (const response of responsesSafe) {
      const answer = response.answers?.[question.id];
      if (isHeatMapAnswer(answer) && answer.imageNaturalWidth && answer.imageNaturalHeight) {
        return {
          width: answer.imageNaturalWidth,
          height: answer.imageNaturalHeight,
        };
      }
    }
    
    // Default fallback
    return { width: 800, height: 600 };
  }, [responsesSafe]);

  // Get individual responses with their clicks for a heat map question (for individual view)
  const getIndividualResponses = useCallback((questionId: string): IndividualResponseData[] => {
    const responses: IndividualResponseData[] = [];
    
    responsesSafe.forEach((response, idx) => {
      const answer = response.answers?.[questionId];
      if (isHeatMapAnswer(answer) && answer.clicks && answer.clicks.length > 0) {
        responses.push({
          responseId: response.id,
          responseIndex: idx + 1,  // 1-based for display
          clicks: answer.clicks,
          submittedAt: response.createdAt,
        });
      }
    });
    
    return responses;
  }, [responsesSafe]);

  // Toggle expanded state for a heat map question
  const toggleHeatMapExpanded = useCallback((questionId: string) => {
    setExpandedHeatMapQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  }, []);

  // Fetch signed URLs for heat map images (must be before early returns)
  useEffect(() => {
    if (heatMapQuestions.length === 0) return;

    const fetchHeatMapUrls = async () => {
      const urls: Record<string, string> = {};

      for (const question of heatMapQuestions) {
        const imageKey = question.heatMapConfig?.imageKey;
        if (!imageKey) continue;
        if (heatMapSignedUrls[imageKey]) continue; // Already fetched

        // External URLs don't need signing
        if (imageKey.startsWith("http://") || imageKey.startsWith("https://")) {
          urls[imageKey] = imageKey;
          continue;
        }

        // Fetch signed URL for R2 key
        if (imageKey.startsWith("survey-uploads/")) {
          try {
            const apiUrl = API_URL
              ? `${API_URL}/api/surveys/${surveyId}/files/signed-url`
              : `/api/surveys/${surveyId}/files/signed-url`;

            const token = getAuthToken();
            const response = await fetch(apiUrl, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ key: imageKey }),
            });

            if (response.ok) {
              const data = await response.json();
              urls[imageKey] = data.url;
            }
          } catch (error) {
            console.error("Failed to fetch signed URL for heat map:", error);
          }
        }
      }

      if (Object.keys(urls).length > 0) {
        setHeatMapSignedUrls((prev) => ({ ...prev, ...urls }));
      }
    };

    fetchHeatMapUrls();
  }, [heatMapQuestions, surveyId, heatMapSignedUrls]);

  // Loading state - show page structure with skeleton rows
  if (isLoading || authLoading) {
    return (
      <AppShell maxWidth="wide">
        {/* Skeleton PageHeader */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Skeleton className="h-4 w-16" />
            <span>/</span>
            <Skeleton className="h-4 w-24" />
            <span>/</span>
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>

        <div className="space-y-6">
          {/* Skeleton Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-8 w-16" />
                    </div>
                    <Skeleton className="h-12 w-12 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Skeleton Table - uses simple divs instead of Table components */}
          <div className="rounded-lg border border-border overflow-hidden">
            {/* Search skeleton */}
            <div className="p-4">
              <Skeleton className="h-9 w-80" />
            </div>
            {/* Table header skeleton */}
            <div className="bg-muted/50 px-4 py-3 flex gap-4 border-b border-border">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-32 flex-1" />
              <Skeleton className="h-4 w-32 flex-1" />
              <Skeleton className="h-4 w-32 flex-1" />
              <Skeleton className="h-4 w-16" />
            </div>
            {/* Table rows skeleton */}
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="px-4 py-3 flex gap-4 border-b border-border last:border-b-0">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-8 w-14" />
              </div>
            ))}
            {/* Pagination skeleton */}
            <div className="border-t border-border py-4 flex items-center justify-center">
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-500 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Unable to Load Responses</h2>
            <p className="text-muted-foreground mb-4">
              {error instanceof Error ? error.message : "Something went wrong"}
            </p>
            <Link to="/projects">
              <Button>Back to Projects</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { stats } = data;

  // Format date helper
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Type for file upload answers
  interface FileAnswer {
    key: string;
    filename: string;
    size: number;
    mimetype: string;
    duration?: number;
  }

  // Check if an answer is a file upload (includes videos) - type guard
  const isFileUploadAnswer = (answer: unknown): answer is FileAnswer => {
    return answer !== null && typeof answer === "object" && "key" in answer && "filename" in answer;
  };

  // Check if an answer is a video upload specifically
  const isVideoAnswer = (answer: unknown): answer is FileAnswer => {
    return isFileUploadAnswer(answer) && answer.mimetype?.startsWith("video/");
  };

  // Helper to format image choice answers with captions
  // Answers are stored as indices ("0", "1", "2") or "__none__" for "None of the above"
  const formatImageChoiceAnswer = (answer: unknown, question: QuestionType): string => {
    const imageOptions = question.imageOptions;
    if (!imageOptions) return String(answer);
    
    const noneLabel = imageOptions.noneOfAboveLabel || "None of the above";
    
    // Helper to get caption for an index (answers are stored as "0", "1", etc.)
    const getCaptionForIndex = (indexStr: string): string => {
      if (indexStr === "__none__") return noneLabel;
      
      const index = parseInt(indexStr, 10);
      if (isNaN(index) || index < 0 || index >= imageOptions.images.length) {
        return `Image ${indexStr}`;
      }
      
      const image = imageOptions.images[index];
      return image?.caption || image?.altText || `Image ${index + 1}`;
    };
    
    if (Array.isArray(answer)) {
      // Multiple selection
      return answer.map(item => getCaptionForIndex(String(item))).join(", ");
    } else if (typeof answer === "string") {
      // Single selection
      return getCaptionForIndex(answer);
    }
    
    return String(answer);
  };

  // Format answer for display
  const formatAnswer = (answer: unknown, question: QuestionType): string => {
    if (answer === undefined || answer === null) return "—";
    
    // Handle video upload answers
    if (isVideoAnswer(answer)) {
      return `🎥 ${answer.filename}`;
    }
    
    // Handle file upload answers - return filename for simple text display
    if (isFileUploadAnswer(answer)) {
      return `📎 ${answer.filename}`;
    }
    
    // Handle image choice answers
    if (question.type === "image_choice") {
      return formatImageChoiceAnswer(answer, question);
    }
    
    // Handle option-based questions - resolve IDs to labels
    const optionBasedTypes = ["single_choice", "multiple_choice", "dropdown", "likert_scale", "ranking"];
    if (optionBasedTypes.includes(question.type) && question.options) {
      const resolveOptionLabel = (optionId: string): string => {
        // Handle "Other" responses - stored as "__other__:<text>"
        if (optionId.startsWith("__other__:")) {
          return optionId.replace("__other__:", "Other: ");
        }
        // Handle bare "__other__" (no text entered)
        if (optionId === "__other__") {
          return "Other";
        }
        const option = question.options?.find(o => o.id === optionId);
        return option?.text ?? optionId;
      };
      
      if (Array.isArray(answer)) {
        return answer.map(id => resolveOptionLabel(String(id))).join(", ");
      }
      return resolveOptionLabel(String(answer));
    }
    
    if (Array.isArray(answer)) {
      return answer.join(", ");
    }
    
    if (typeof answer === "object") {
      // Handle matrix-type answers with human-readable labels
      if (question.type.includes("matrix") || question.type.includes("grid")) {
        return formatMatrixAnswer(answer as Record<string, unknown>, question);
      }
      return JSON.stringify(answer);
    }
    
    return String(answer);
  };

  // Format matrix answers with row and column labels
  const formatMatrixAnswer = (answer: Record<string, unknown>, question: QuestionType): string => {
    const rows = question.rows || [];
    const columns = question.columns || [];
    
    const lines: string[] = [];
    
    // Handle radio_grid and checkbox_grid (key is row index, value is column index or array of column indices)
    if (question.type === "radio_grid" || question.type === "checkbox_grid") {
      Object.entries(answer).forEach(([rowIdx, colValue]) => {
        const rowIndex = parseInt(rowIdx);
        const rowLabel = rows[rowIndex]?.label || `Row ${rowIndex + 1}`;
        
        if (question.type === "radio_grid") {
          // Single selection per row
          const colIndex = colValue as number;
          const colLabel = columns[colIndex]?.label || `Option ${colIndex + 1}`;
          lines.push(`${rowLabel}: ${colLabel}`);
        } else {
          // Multiple selections per row (checkbox_grid)
          const colIndices = Array.isArray(colValue) ? colValue : [colValue];
          const colLabels = colIndices.map((ci: number) => columns[ci]?.label || `Option ${ci + 1}`);
          lines.push(`${rowLabel}: ${colLabels.join(", ")}`);
        }
      });
    } 
    // Handle dropdown_matrix, text_matrix, numeric_matrix, ranking_matrix (key is "rowIndex-colIndex")
    else if (question.type === "dropdown_matrix" || question.type === "text_matrix" || 
             question.type === "numeric_matrix" || question.type === "ranking_matrix") {
      // Group answers by row
      const rowAnswers: Record<number, { col: number; value: string }[]> = {};
      
      Object.entries(answer).forEach(([key, value]) => {
        const [rowIdx, colIdx] = key.split("-").map(Number);
        if (!rowAnswers[rowIdx]) rowAnswers[rowIdx] = [];
        rowAnswers[rowIdx].push({ col: colIdx, value: String(value) });
      });
      
      // Sort by row index and format
      Object.keys(rowAnswers)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((rowIdx) => {
          const rowLabel = rows[rowIdx]?.label || `Row ${rowIdx + 1}`;
          const colAnswers = rowAnswers[rowIdx]
            .sort((a, b) => a.col - b.col)
            .map(({ col, value }) => {
              const colLabel = columns[col]?.label || `Col ${col + 1}`;
              return `${colLabel}: ${value}`;
            })
            .join(", ");
          lines.push(`${rowLabel} → ${colAnswers}`);
        });
    }
    
    return lines.join("\n");
  };

  // Check if question is a matrix type
  const isMatrixQuestion = (type: string): boolean => {
    return ["radio_grid", "checkbox_grid", "dropdown_matrix", "text_matrix", "numeric_matrix", "ranking_matrix"].includes(type);
  };

  // Image Choice Answer Display Component - shows selected images with thumbnails
  // Answers are stored as indices ("0", "1", "2") or "__none__" for "None of the above"
  const ImageChoiceAnswerDisplay = ({ answer, question }: { answer: string | string[]; question: QuestionType }) => {
    const imageOptions = question.imageOptions;
    const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
    
    // Get list of selected indices (answers are stored as "0", "1", "__none__", etc.)
    const selectedIndices = Array.isArray(answer) ? answer : [answer];
    const noneLabel = imageOptions?.noneOfAboveLabel || "None of the above";
    
    // Create a stable key from the answer to track what we've fetched
    const answerKey = JSON.stringify(answer);
    const fetchedForKeyRef = useRef<string>("");
    
    // Fetch signed URLs for selected images
    useEffect(() => {
      if (!imageOptions) return;
      // Only fetch if this answer key is different from what we've fetched for
      if (fetchedForKeyRef.current === answerKey) return;
      fetchedForKeyRef.current = answerKey;
      
      const indices = Array.isArray(answer) ? answer : [answer];
      
      const fetchUrls = async () => {
        const urls: Record<string, string> = {};
        
        for (const indexStr of indices) {
          if (indexStr === "__none__") continue;
          
          const index = parseInt(indexStr, 10);
          if (isNaN(index) || index < 0 || index >= imageOptions.images.length) continue;
          
          const imageUrl = imageOptions.images[index]?.url;
          if (!imageUrl) continue;
          
          // Check if it's an external URL
          if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
            urls[indexStr] = imageUrl;
          } else if (imageUrl.startsWith("survey-uploads/")) {
            // Fetch signed URL for R2 key
            try {
              const apiUrl = API_URL 
                ? `${API_URL}/api/surveys/${surveyId}/files/signed-url`
                : `/api/surveys/${surveyId}/files/signed-url`;
              
              const token = getAuthToken();
              const response = await fetch(apiUrl, {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ key: imageUrl }),
              });

              if (response.ok) {
                const data = await response.json();
                urls[indexStr] = data.url;
              }
            } catch (error) {
              console.error("Failed to fetch signed URL for image:", error);
            }
          }
        }
        
        setSignedUrls(urls);
      };
      
      fetchUrls();
    }, [answer, answerKey, imageOptions]);
    
    if (!imageOptions) {
      return <p className="text-foreground">{formatImageChoiceAnswer(answer, question)}</p>;
    }
    
    return (
      <div className="space-y-2">
        {selectedIndices.map((indexStr, idx) => {
          // Handle "None of the above" selection
          if (indexStr === "__none__") {
            return (
              <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                <div className="w-10 h-10 bg-muted-foreground/20 rounded flex items-center justify-center">
                  <X className="h-5 w-5 text-muted-foreground" />
                </div>
                <span className="text-foreground text-sm font-medium">{noneLabel}</span>
              </div>
            );
          }
          
          // Get the image by index
          const imageIndex = parseInt(indexStr, 10);
          const image = !isNaN(imageIndex) && imageIndex >= 0 && imageIndex < imageOptions.images.length
            ? imageOptions.images[imageIndex]
            : null;
          const caption = image?.caption || image?.altText || `Image ${imageIndex + 1}`;
          const signedUrl = signedUrls[indexStr];
          
          return (
            <div key={idx} className="flex items-center gap-3 p-2 bg-card border border-border rounded-lg">
              {signedUrl ? (
                <img 
                  src={signedUrl} 
                  alt={caption}
                  className="w-12 h-12 object-cover rounded"
                />
              ) : (
                <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-foreground text-sm font-medium truncate">{caption}</p>
                {image?.altText && image.altText !== caption && (
                  <p className="text-muted-foreground text-xs truncate">{image.altText}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Heat Map Answer Display Component - shows image with click dots
  const HeatMapAnswerDisplay = ({ answer, question }: { answer: HeatMapAnswer; question: QuestionType }) => {
    const config = question.heatMapConfig;
    const imageKey = config?.imageKey || "";
    const [signedUrl, setSignedUrl] = useState<string | null>(null);
    const fetchedKeyRef = useRef<string>("");
    
    // Fetch signed URL for the heat map image
    useEffect(() => {
      if (!imageKey) return;
      if (fetchedKeyRef.current === imageKey) return;
      fetchedKeyRef.current = imageKey;
      
      // External URLs don't need signing
      if (imageKey.startsWith("http://") || imageKey.startsWith("https://")) {
        setSignedUrl(imageKey);
        return;
      }
      
      // Fetch signed URL for R2 key
      if (imageKey.startsWith("survey-uploads/")) {
        const fetchUrl = async () => {
          try {
            const apiUrl = API_URL 
              ? `${API_URL}/api/surveys/${surveyId}/files/signed-url`
              : `/api/surveys/${surveyId}/files/signed-url`;
            
            const token = getAuthToken();
            const response = await fetch(apiUrl, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ key: imageKey }),
            });

            if (response.ok) {
              const data = await response.json();
              setSignedUrl(data.url);
            }
          } catch (error) {
            console.error("Failed to fetch signed URL for heat map:", error);
          }
        };
        
        fetchUrl();
      }
    }, [imageKey]);
    
    const clicks = answer.clicks || [];
    
    return (
      <div className="space-y-3">
        {/* Image with click dots */}
        <div className="relative inline-block">
          {signedUrl ? (
            <>
              <img 
                src={signedUrl}
                alt="Heat map response"
                className="max-w-full h-auto rounded-lg border border-border"
                style={{ maxHeight: "300px" }}
              />
              
              {/* Click dots overlay */}
              {clicks.map((click) => {
                // Look up region name for tooltip
                const regionName = click.regionId === "other" 
                  ? "outside regions" 
                  : click.regionId 
                    ? config?.regions?.find(r => r.id === click.regionId)?.name || click.regionId
                    : null;
                
                return (
                <div
                  key={click.slot}
                  className="absolute w-4 h-4 rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: `${click.xPct}%`,
                    top: `${click.yPct}%`,
                    backgroundColor: '#e88463',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                  title={`Click ${click.slot}: (${click.xPx}, ${click.yPx})${regionName ? ` - ${regionName}` : ''}`}
                >
                  {clicks.length > 1 && (
                    <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-white">
                      {click.slot}
                    </span>
                  )}
                </div>
              );
              })}
            </>
          ) : imageKey ? (
            <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
          ) : (
            <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center">
              <p className="text-muted-foreground text-sm">No image configured</p>
            </div>
          )}
        </div>
        
        {/* Click coordinates table */}
        {clicks.length > 0 && (
          <div className="mt-3 border border-border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="px-2 py-1 text-left font-medium text-muted-foreground">Click</th>
                  <th className="px-2 py-1 text-left font-medium text-muted-foreground">X (px)</th>
                  <th className="px-2 py-1 text-left font-medium text-muted-foreground">Y (px)</th>
                  {config?.regions && config.regions.length > 0 && (
                    <th className="px-2 py-1 text-left font-medium text-muted-foreground">Region</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {clicks.map((click) => (
                  <tr key={click.slot} className="border-t border-border">
                    <td className="px-2 py-1 text-foreground">{click.slot}</td>
                    <td className="px-2 py-1 text-foreground">{click.xPx}</td>
                    <td className="px-2 py-1 text-foreground">{click.yPx}</td>
                    {config?.regions && config.regions.length > 0 && (
                      <td className="px-2 py-1 text-foreground">
                        {click.regionId === "other" ? (
                          <span className="text-muted-foreground italic">outside regions</span>
                        ) : click.regionId ? (
                          // Look up region name from config
                          config.regions.find(r => r.id === click.regionId)?.name || click.regionId
                        ) : (
                          "-"
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {clicks.length === 0 && (
          <p className="text-muted-foreground text-sm italic">No clicks recorded</p>
        )}
      </div>
    );
  };

  // Pick, Group & Rank Answer Display Component - shows grouped and ranked items
  const PickGroupRankAnswerDisplay = ({ answer, question }: { answer: PickGroupRankAnswer; question: QuestionType }) => {
    const config = question.pickGroupRankConfig;
    if (!config) return <p className="text-muted-foreground italic">No configuration</p>;
    
    const groups = config.groups || [];
    const allowRanking = config.allowRankingWithinGroups ?? false;
    
    // Helper to get item label by ID
    const getLabelById = (itemId: string) => getItemLabel(config, itemId);
    
    return (
      <div className="space-y-4">
        {/* Groups */}
        {groups.map((group) => {
          const groupItems = answer.groups[group.id] || [];
          
          return (
            <div key={group.id} className="border border-border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-muted font-medium text-sm text-foreground flex items-center justify-between">
                <span>{group.label}</span>
                <span className="text-xs text-muted-foreground">
                  {groupItems.length} item{groupItems.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="p-2">
                {groupItems.length > 0 ? (
                  allowRanking ? (
                    <ol className="list-decimal list-inside space-y-1">
                      {groupItems.map((itemId, index) => (
                        <li key={itemId} className="text-sm text-foreground">
                          <span className="ml-1">{getLabelById(itemId)}</span>
                          <span className="text-xs text-muted-foreground ml-2">#{index + 1}</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <ul className="list-disc list-inside space-y-1">
                      {groupItems.map((itemId) => (
                        <li key={itemId} className="text-sm text-foreground">
                          {getLabelById(itemId)}
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground italic">No items in this group</p>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Unsorted items (if any) */}
        {answer.unsorted && answer.unsorted.length > 0 && (
          <div className="border border-dashed border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-muted font-medium text-sm text-muted-foreground flex items-center justify-between">
              <span>Unsorted Items</span>
              <span className="text-xs text-muted-foreground/70">
                {answer.unsorted.length} item{answer.unsorted.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="p-2">
              <ul className="list-disc list-inside space-y-1">
                {answer.unsorted.map((itemId) => (
                  <li key={itemId} className="text-sm text-muted-foreground">
                    {getLabelById(itemId)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Matrix Answer Display Component - renders a nice table instead of plain text
  const MatrixAnswerDisplay = ({ answer, question }: { answer: Record<string, unknown>; question: QuestionType }) => {
    const rows = question.rows || [];
    const columns = question.columns || [];

    // Process the answer data into displayable format
    const getDisplayData = (): { rowLabel: string; values: string[] }[] => {
      const data: { rowLabel: string; values: string[] }[] = [];

      if (question.type === "radio_grid" || question.type === "checkbox_grid") {
        Object.entries(answer).forEach(([rowIdx, colValue]) => {
          const rowIndex = parseInt(rowIdx);
          const rowLabel = rows[rowIndex]?.label || `Row ${rowIndex + 1}`;
          
          if (question.type === "radio_grid") {
            const colIndex = colValue as number;
            const colLabel = columns[colIndex]?.label || `Option ${colIndex + 1}`;
            data.push({ rowLabel, values: [colLabel] });
          } else {
            const colIndices = Array.isArray(colValue) ? colValue : [colValue];
            const colLabels = colIndices.map((ci: number) => columns[ci]?.label || `Option ${ci + 1}`);
            data.push({ rowLabel, values: colLabels });
          }
        });
      } else {
        // dropdown_matrix, text_matrix, numeric_matrix, ranking_matrix
        const rowAnswers: Record<number, { col: number; value: string }[]> = {};
        
        Object.entries(answer).forEach(([key, value]) => {
          const [rowIdx, colIdx] = key.split("-").map(Number);
          if (!rowAnswers[rowIdx]) rowAnswers[rowIdx] = [];
          rowAnswers[rowIdx].push({ col: colIdx, value: String(value) });
        });
        
        Object.keys(rowAnswers)
          .map(Number)
          .sort((a, b) => a - b)
          .forEach((rowIdx) => {
            const rowLabel = rows[rowIdx]?.label || `Row ${rowIdx + 1}`;
            const values = rowAnswers[rowIdx]
              .sort((a, b) => a.col - b.col)
              .map(({ col, value }) => {
                const colLabel = columns[col]?.label || `Col ${col + 1}`;
                return `${colLabel}: ${value}`;
              });
            data.push({ rowLabel, values });
          });
      }

      return data;
    };

    const displayData = getDisplayData();

    return (
      <div className="space-y-1">
        {displayData.map((item, idx) => (
          <div 
            key={idx} 
            className={`flex items-start gap-3 p-2 rounded ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/50'}`}
          >
            <span className="text-foreground font-medium text-sm min-w-0 flex-shrink-0 max-w-[40%]">
              {item.rowLabel}
            </span>
            <span className="text-muted-foreground flex-shrink-0">→</span>
            <div className="flex flex-wrap gap-1.5 min-w-0">
              {item.values.map((value, vIdx) => (
                <span 
                  key={vIdx} 
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                >
                  {value}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Pagination calculations - use filteredResponses for search support
  const totalPages = Math.ceil(filteredResponses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResponses = filteredResponses.slice(startIndex, endIndex);
  
  // Handle page changes (used by DataTable)
  const goToPage = (page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  return (
    <AppShell maxWidth="wide">
      <PageHeader
        title={survey.title}
        subtitle={`${stats.total} responses · ${survey.questions.length} questions`}
        breadcrumbs={[
          { label: "Projects", href: "/projects" },
          { label: survey.title, href: `/survey/${surveyId}` },
          { label: "Responses" }
        ]}
        actions={
          <div className="flex items-center gap-2">
            {/* Status Badge */}
            <Badge variant={survey.status === "published" ? "default" : "secondary"}>
              {survey.status}
            </Badge>
            
            {/* View Quotas Button */}
            {survey.settings?.quotasEnabled && (
              <Link to={`/survey/${surveyId}/quotas`}>
                <Button variant="outline" size="sm" className="gap-2" aria-label="View quota management">
                  <PieChart className="h-4 w-4" />
                  View Quotas
                </Button>
              </Link>
            )}
            
            {/* Export Button */}
            {responsesSafe.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportDialog(true)}
                aria-label="Export responses"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
          </div>
        }
      />

      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Responses"
            value={stats.total}
            icon={<Users className="h-6 w-6 text-primary" />}
          />
          <StatCard
            label="Completed"
            value={stats.completed}
            icon={<CheckCircle2 className="h-6 w-6 text-green-500" />}
          />
          <StatCard
            label="Partial"
            value={stats.total - stats.completed}
            icon={<FileText className="h-6 w-6 text-amber-500" />}
          />
          <StatCard
            label="Completion Rate"
            value={`${stats.completionRate}%`}
            icon={<PieChart className="h-6 w-6 text-blue-500" />}
          />
        </div>

        {/* Heat Map Aggregate Visualization (Phase 3) */}
        {heatMapQuestions.length > 0 && responsesSafe.length > 0 && (
          <div className="space-y-4 mb-8">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Heat Map Analysis
            </h2>
            
            {heatMapQuestions.map((question) => {
              const imageKey = question.heatMapConfig?.imageKey;
              const signedUrl = imageKey ? heatMapSignedUrls[imageKey] : null;
              const aggregateClicks = getAggregateClicks(question.id);
              const dimensions = getHeatMapDimensions(question);
              const isExpanded = expandedHeatMapQuestions.has(question.id);
              
              // Count responses with clicks for this question
              const responsesWithClicks = responsesSafe.filter((r) => {
                const answer = r.answers?.[question.id];
                return isHeatMapAnswer(answer) && answer.clicks?.length > 0;
              }).length;
              
              return (
                <Card key={question.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">{question.question}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {aggregateClicks.length} click{aggregateClicks.length !== 1 ? "s" : ""} from {responsesWithClicks} response{responsesWithClicks !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleHeatMapExpanded(question.id)}
                      >
                        {isExpanded ? "Collapse" : "Expand"}
                      </Button>
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent>
                      {signedUrl && question.heatMapConfig ? (
                        <HeatMapVisualization
                          imageUrl={signedUrl}
                          clicks={aggregateClicks}
                          config={question.heatMapConfig}
                          imageNaturalWidth={dimensions.width}
                          imageNaturalHeight={dimensions.height}
                          individualResponses={getIndividualResponses(question.id)}
                        />
                      ) : imageKey && !signedUrl ? (
                        <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
                          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
                          <p className="text-muted-foreground text-sm">No image configured</p>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Search results indicator */}
        {debouncedSearch && (
          <p 
            className="text-sm text-muted-foreground mb-3" 
            role="status" 
            aria-live="polite"
          >
            {filteredResponses.length === 0 ? (
              <>No responses match "{debouncedSearch}"</>
            ) : (
              <>Found {filteredResponses.length} of {responsesSafe.length} responses</>
            )}
          </p>
        )}

        {/* Responses Table using DataTable component */}
        <DataTable
          data={paginatedResponses}
          columns={[
            {
              key: "id",
              header: "ID",
              width: "w-16",
              render: (response) => (
                <span className="font-medium">{response.id}</span>
              ),
            },
            {
              key: "submitted",
              header: "Submitted",
              render: (response) => formatDate(response.completedAt || response.createdAt),
            },
            {
              key: "status",
              header: "Status",
              render: (response) => (
                <Badge 
                  variant={response.isComplete ? "default" : "secondary"}
                  className={response.isComplete ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300" : ""}
                >
                  {response.isComplete ? "Complete" : "Partial"}
                </Badge>
              ),
            },
            // Preview columns for first 3 questions
            ...survey.questions.slice(0, 3).map((q) => ({
              key: `preview-${q.id}`,
              header: q.question,
              width: "max-w-[200px]",
              render: (response: SurveyResponse) => {
                const answerText = formatAnswer(response.answers?.[q.id], q);
                return (
                  <span className="truncate block text-sm text-muted-foreground" title={answerText}>
                    {answerText.substring(0, 50)}
                    {answerText.length > 50 ? "..." : ""}
                  </span>
                );
              },
            })),
            {
              key: "actions",
              header: "Actions",
              align: "right" as const,
              render: (response) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedResponse(response);
                  }}
                  aria-label={`View response ${response.id}`}
                >
                  View
                </Button>
              ),
            },
          ]}
          keyAccessor={(response) => response.id}
          onRowClick={(response) => setSelectedResponse(response)}
          searchable
          searchPlaceholder="Search responses..."
          searchValue={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          searchKeyboardShortcuts
          searchEnhanced
          pagination={{
            page: currentPage,
            pageSize: itemsPerPage,
            totalCount: filteredResponses.length,
            unfilteredCount: responsesSafe.length,
            onPageChange: goToPage,
          }}
          extendedPagination
          onPageSizeChange={(pageSize) => {
            setItemsPerPage(pageSize);
            setCurrentPage(1);
          }}
          isLoading={isFetching && !isLoading}
          skeletonRowCount={itemsPerPage}
          emptyState={
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No responses yet</h3>
              <p className="text-muted-foreground mb-4">
                Share your survey to start collecting responses
              </p>
              <Link to={`/survey/${surveyId}`}>
                <Button>Go to Survey Editor</Button>
              </Link>
            </div>
          }
        />
      </div>

      {/* Export Options Dialog */}
      <ExportOptionsDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onExport={handleExport}
        surveyTitle={survey.title}
      />

      {/* Response Detail Dialog - Redesigned per guide spec */}
      <Dialog open={!!selectedResponse} onOpenChange={() => setSelectedResponse(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden">
          {selectedResponse && (
            <>
              {/* Sticky Header */}
              <DialogHeader className="px-6 py-4 border-b border-border sticky top-0 bg-background z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <DialogTitle className="text-lg">
                      Response #{selectedResponse.id}
                      {selectedResponse.respondentId && (
                        <span className="text-muted-foreground font-normal ml-2">
                          ({selectedResponse.respondentId})
                        </span>
                      )}
                    </DialogTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge 
                        variant={selectedResponse.isComplete ? "default" : "secondary"}
                        className={selectedResponse.isComplete ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300" : ""}
                      >
                        {selectedResponse.isComplete ? (
                          <><CheckCircle2 className="h-3 w-3 mr-1" /> Complete</>
                        ) : (
                          <><AlertCircle className="h-3 w-3 mr-1" /> Partial</>
                        )}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <ScrollArea className="max-h-[calc(90vh-140px)] overflow-hidden">
                <div className="px-6 py-4 space-y-4">
                  {/* Metadata Card */}
                  <Card className="bg-muted/50">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Started:</span>
                          <span>{formatDate(selectedResponse.createdAt)}</span>
                        </div>
                        {selectedResponse.completedAt && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Completed:</span>
                            <span>{formatDate(selectedResponse.completedAt)}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Questions & Answers */}
                  <div className="space-y-4">
                    {survey.questions.map((question, index) => {
                      const answer = selectedResponse.answers?.[question.id];
                      return (
                        <Card key={question.id}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center flex-shrink-0">
                                {index + 1}
                              </span>
                              <span className="flex-1">{question.question}</span>
                              {question.required && (
                                <span className="text-destructive">*</span>
                              )}
                            </CardTitle>
                            <Badge variant="outline" className="w-fit text-xs capitalize">
                              {question.type.replace(/_/g, " ")}
                            </Badge>
                          </CardHeader>
                          <CardContent>
                            <div className="bg-muted/50 rounded-lg p-3">
                              {answer !== undefined && answer !== null && answer !== "" ? (
                                isFileUploadAnswer(answer) ? (
                                  <FileAnswerDisplay 
                                    answer={answer} 
                                    surveyId={surveyId}
                                    responseId={selectedResponse.id}
                                  />
                                ) : question.type === "image_choice" ? (
                                  <ImageChoiceAnswerDisplay
                                    answer={answer as string | string[]}
                                    question={question}
                                  />
                                ) : question.type === "heat_map" && isHeatMapAnswer(answer) ? (
                                  <HeatMapAnswerDisplay
                                    answer={answer}
                                    question={question}
                                  />
                                ) : question.type === "pick_group_rank" && isPickGroupRankAnswer(answer) ? (
                                  <PickGroupRankAnswerDisplay
                                    answer={answer}
                                    question={question}
                                  />
                                ) : isMatrixQuestion(question.type) && typeof answer === "object" ? (
                                  <MatrixAnswerDisplay 
                                    answer={answer as Record<string, unknown>} 
                                    question={question} 
                                  />
                                ) : (
                                  <p className="text-foreground whitespace-pre-wrap">
                                    {formatAnswer(answer, question)}
                                  </p>
                                )
                              ) : (
                                <p className="text-muted-foreground italic">No answer provided</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Additional Metadata - Polished Grid */}
                  {selectedResponse.metadata && Object.keys(selectedResponse.metadata as Record<string, unknown>).length > 0 && (
                    <Card className="bg-muted/30">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          Response Metadata
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {Object.entries(selectedResponse.metadata as Record<string, unknown>)
                            .filter(([, value]) => value !== null && value !== undefined && value !== '')
                            .map(([key, value]) => (
                              <div 
                                key={key} 
                                className="bg-background rounded-md px-3 py-2 border border-border/50"
                              >
                                <p className="text-xs text-muted-foreground capitalize mb-0.5">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </p>
                                <p className="text-sm text-foreground font-medium truncate" title={String(value)}>
                                  {typeof value === 'string' ? value.substring(0, 80) : String(value)}
                                  {typeof value === 'string' && value.length > 80 ? '...' : ''}
                                </p>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

